<template>
  <Teleport to="body">
    <!-- Mobile Chat Widget Container -->
    <div class="mobile-chat-widget" :data-theme="theme">
      <!-- Floating Action Button -->
      <Transition name="mobile-fab" appear>
        <button
          v-if="!isOpen"
          @touchstart="handleTouchStart"
          @touchend="handleTouchEnd"
          class="mobile-fab"
          :class="{ 'mobile-fab--pulsing': showPulse }"
          :aria-label="ariaLabel"
        >
          <div class="mobile-fab__icon-container">
            <Icon 
              :icon="isLoading ? 'line-md:loading-twotone-loop' : 'mdi:headset'" 
              class="mobile-fab__icon" 
              :class="{ 'mobile-fab__icon--loading': isLoading }"
            />
            <!-- Online Status Indicator -->
            <div 
              v-if="isBusinessOpen && !isLoading" 
              class="mobile-fab__status-indicator"
            ></div>
            <!-- Unread Message Badge -->
            <div 
              v-if="unreadCount > 0" 
              class="mobile-fab__badge"
            >
              {{ unreadCount > 99 ? '99+' : unreadCount }}
            </div>
          </div>
        </button>
      </Transition>

      <!-- Full-Screen Mobile Panel -->
      <Transition name="mobile-panel">
        <div
          v-if="isOpen"
          ref="mobilePanel"
          class="mobile-panel"
          data-swipeable
          role="dialog"
          aria-labelledby="mobile-panel-title"
        >
          <!-- Panel Header -->
          <div class="mobile-panel__header">
            <div class="mobile-panel__swipe-indicator"></div>
            
            <div class="mobile-panel__top">
              <div class="mobile-panel__avatar">
                <img 
                  :src="agentAvatar" 
                  :alt="agentName"
                  class="mobile-panel__avatar-img"
                  @error="onAvatarError"
                />
                <div 
                  v-if="isBusinessOpen" 
                  class="mobile-panel__status-dot"
                ></div>
              </div>
              <div class="mobile-panel__info">
                <h3 id="mobile-panel-title" class="mobile-panel__title">{{ contactTitle }}</h3>
                <p class="mobile-panel__subtitle">{{ contactSubtitle }}</p>
              </div>
              <button
                @click="closeWidget"
                @touchstart.stop
                @touchend.stop="closeWidget"
                class="mobile-panel__close"
                aria-label="Close contact panel"
              >
                <Icon icon="mdi:close" />
              </button>
            </div>
          </div>

          <!-- Contact Options -->
          <div class="mobile-options">
            <!-- Chat Option -->
            <button
              @click="handleChatClick"
              class="mobile-option mobile-option--chat"
              :class="{ 'mobile-option--closed': !isBusinessOpen }"
              :disabled="isLoading || !isBusinessOpen"
            >
              <div class="mobile-option__icon">
                <Icon 
                  v-if="isLoading" 
                  icon="line-md:loading-twotone-loop" 
                  class="mobile-option__loading" 
                />
                <Icon v-else :icon="isBusinessOpen ? 'mdi:chat-processing' : 'mdi:chat-sleep'" />
              </div>
              <div class="mobile-option__content">
                <h4 class="mobile-option__title">Start Live Chat</h4>
                <p class="mobile-option__subtitle">{{ isBusinessOpen ? businessStatus.actionText : 'Currently closed - Call or email us instead' }}</p>
              </div>
              <div class="mobile-option__arrow" v-if="isBusinessOpen">
                <Icon icon="mdi:chevron-right" />
              </div>
              <div class="mobile-option__badge mobile-option__badge--closed" v-else>
                <span class="mobile-option__badge-text">Closed</span>
              </div>
            </button>

            <!-- Quick Actions -->
            <div class="mobile-actions" :class="{ 'mobile-actions--primary': !isBusinessOpen }">
              <button
                @click="handlePhoneClick"
                class="mobile-action mobile-action--phone"
                :class="{ 'mobile-action--primary': !isBusinessOpen }"
              >
                <div class="mobile-action__icon">
                  <Icon icon="mdi:phone" />
                </div>
                <span class="mobile-action__label">{{ !isBusinessOpen ? 'Call Now' : 'Call' }}</span>
                <span v-if="!isBusinessOpen" class="mobile-action__sublabel">{{ phoneNumber }}</span>
              </button>

              <button
                @click="handleEmailClick"
                class="mobile-action mobile-action--email"
                :class="{ 'mobile-action--primary': !isBusinessOpen }"
              >
                <div class="mobile-action__icon">
                  <Icon icon="mdi:email" />
                </div>
                <span class="mobile-action__label">{{ !isBusinessOpen ? 'Email Now' : 'Email' }}</span>
                <span v-if="!isBusinessOpen" class="mobile-action__sublabel">Send us a message</span>
              </button>
            </div>
          </div>

          <!-- Business Hours -->
          <div class="mobile-panel__footer">
            <BusinessHours :business-hours="businessHours" />
          </div>
        </div>
      </Transition>
    </div>
    
    <!-- Mobile Notification System -->
    <NotificationSystem :is-mobile="true" />
  </Teleport>
</template>

<script>
import { ref, computed } from 'vue';
import { Icon } from '@iconify/vue';
import { useLocalStorage } from '@/composables/useLocalStorage';
import { useBusinessHours } from '@/composables/chat/useBusinessHours';
import { useContactMethods } from '@/composables/chat/useContactMethods';
import { useNotifications } from '@/composables/ui/useNotifications';
import { useSwipeGestures } from '@/composables/ui/useSwipeGestures';
import BusinessHours from '../shared/BusinessHours.vue';
import NotificationSystem from '../shared/NotificationSystem.vue';
import customerServiceAvatar from '@/assets/images/ui/customer-service-avatar.svg';

export default {
  name: 'MobileWidget',
  components: {
    Icon,
    BusinessHours,
    NotificationSystem
  },
  props: {
    theme: {
      type: String,
      default: 'default'
    },
    phoneNumber: {
      type: String,
      default: '(954) 840-8866'
    },
    emailAddress: {
      type: String,
      default: 'policychanges@insuranceexpress.com'
    },
    businessHours: {
      type: String,
      default: 'Mon-Fri 9AM-5PM EST'
    }
  },
  emits: ['close', 'open', 'chat-clicked'],
  setup(props, { emit }) {
    // State
    const isOpen = ref(false);
    const isLoading = ref(false);
    const showPulse = ref(false);
    const unreadCount = ref(0);
    const mobilePanel = ref(null);
    
    // Composables
    const { markAsInteracted } = useLocalStorage();
    const { isBusinessOpen, businessStatus } = useBusinessHours();
    const { makePhoneCall, sendEmail } = useContactMethods();
    const { showNotification } = useNotifications();
    
    // Setup swipe gestures with proper target and callback
    const { isSwipeActive } = useSwipeGestures(mobilePanel, {
      dismissThreshold: 100,
      onDismiss: () => {
        closeWidget();
      }
    });
    
    // Computed
    const agentName = computed(() => 'Insurance Express Support');
    const agentAvatar = computed(() => customerServiceAvatar);
    const contactTitle = computed(() => 'How can we help?');
    const contactSubtitle = computed(() => 'Choose your preferred contact method');
    const ariaLabel = computed(() => `Open contact options${unreadCount.value > 0 ? ` (${unreadCount.value} unread messages)` : ''}`);

    // Touch handling
    let touchStartTime = 0;
    let touchStartX = 0;
    let touchStartY = 0;
    
    const handleTouchStart = (e) => {
      touchStartTime = Date.now();
      if (e.touches && e.touches[0]) {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
      }
    };
    
    const handleTouchEnd = (e) => {
      const touchDuration = Date.now() - touchStartTime;
      
      if (touchDuration < 500) {
        let deltaX = 0;
        let deltaY = 0;
        
        if (e.changedTouches && e.changedTouches[0]) {
          deltaX = Math.abs(e.changedTouches[0].clientX - touchStartX);
          deltaY = Math.abs(e.changedTouches[0].clientY - touchStartY);
        }
        
        if (deltaX < 20 && deltaY < 20) {
          e.preventDefault();
          e.stopPropagation();
          toggleWidget();
        }
      }
    };

    // Widget methods
    const toggleWidget = () => {
      markAsInteracted();
      showPulse.value = false;
      isOpen.value = !isOpen.value;
      
      emit(isOpen.value ? 'open' : 'close');
    };

    const closeWidget = () => {
      isOpen.value = false;
      emit('close');
    };

    const handleChatClick = async () => {
      emit('chat-clicked');
      isLoading.value = true;
      
      try {
        // Mobile chat popup
        const chatUrl = 'https://insuranceexpresschat.com/widget?website_token=5MUh9hiKzyW9fE3MtxJHnzh9';
        const popup = window.open(
          chatUrl,
          'mobile-chat',
          'width=100%,height=100%,scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no,fullscreen=yes'
        );
        
        if (popup) {
          popup.focus();
          showNotification({
            title: 'Opening Chat...',
            message: 'Chat will open in a new window',
            type: 'info',
            duration: 4000
          });
        } else {
          throw new Error('Popup blocked');
        }
        
      } catch (error) {
        showNotification({
          title: 'Chat Unavailable',
          message: 'Please try calling or emailing us instead.',
          type: 'error',
          duration: 5000
        });
      } finally {
        isLoading.value = false;
      }
    };

    const handlePhoneClick = () => {
      if (makePhoneCall(props.phoneNumber)) {
        setTimeout(() => {
          isOpen.value = false;
          emit('close');
        }, 1000);
      }
    };

    const handleEmailClick = () => {
      if (sendEmail(props.emailAddress)) {
        setTimeout(() => {
          isOpen.value = false;
          emit('close');
        }, 1000);
      }
    };

    const onAvatarError = (event) => {
      event.target.src = customerServiceAvatar;
    };

    return {
      // State
      isOpen,
      isLoading,
      showPulse,
      unreadCount,
      mobilePanel,
      
      // Computed
      agentName,
      agentAvatar,
      contactTitle,
      contactSubtitle,
      ariaLabel,
      isBusinessOpen,
      businessStatus,
      
      // Methods
      handleTouchStart,
      handleTouchEnd,
      handleChatClick,
      handlePhoneClick,
      handleEmailClick,
      onAvatarError,
      closeWidget
    };
  }
};
</script>

<style scoped>
/* Mobile Chat Widget Styles */
.mobile-chat-widget {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 999999;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Responsive positioning */
@media (max-width: 640px) {
  .mobile-chat-widget {
    bottom: 20px;
    right: 16px;
  }
}

@media (min-width: 414px) and (max-width: 768px) and (orientation: portrait) {
  .mobile-chat-widget {
    bottom: 24px;
    right: 20px;
  }
}

@media (max-width: 896px) and (orientation: landscape) and (max-height: 500px) {
  .mobile-chat-widget {
    bottom: 20px;
    right: 120px;
  }
}

/* Mobile FAB */
.mobile-fab {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  background: linear-gradient(135deg, #374151, #4B5563);
  border: none;
  border-radius: 50%;
  box-shadow: 0 8px 32px rgba(55, 65, 81, 0.3);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  outline: none;
  user-select: none;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

@media (min-width: 414px) and (max-width: 768px) {
  .mobile-fab {
    width: 68px;
    height: 68px;
  }
}

.mobile-fab:active {
  transform: scale(0.95);
}

.mobile-fab--pulsing {
  animation: mobile-pulse 2s infinite;
}

.mobile-fab__icon-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-fab__icon {
  width: 32px;
  height: 32px;
  color: white;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

.mobile-fab__status-indicator {
  position: absolute;
  top: -3px;
  right: -3px;
  width: 14px;
  height: 14px;
  background: #4CAF50;
  border: 3px solid white;
  border-radius: 50%;
  animation: pulse-dot 2s infinite;
}

.mobile-fab__badge {
  position: absolute;
  top: -10px;
  right: -10px;
  min-width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #EF4444;
  color: white;
  font-size: 12px;
  font-weight: 600;
  border-radius: 12px;
  padding: 0 6px;
}

/* Mobile Panel */
.mobile-panel {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: white;
  z-index: 999998;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.mobile-panel__header {
  background: white;
  border-bottom: 1px solid #E5E7EB;
  padding: 20px;
  flex-shrink: 0;
}

.mobile-panel__swipe-indicator {
  width: 40px;
  height: 4px;
  background: #D1D5DB;
  border-radius: 2px;
  margin: 0 auto 20px auto;
  animation: swipe-hint 3s ease-in-out infinite;
  animation-delay: 1s;
}

.mobile-panel__top {
  display: flex;
  align-items: center;
  gap: 16px;
}

.mobile-panel__avatar {
  position: relative;
  width: 56px;
  height: 56px;
  flex-shrink: 0;
}

.mobile-panel__avatar-img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.mobile-panel__status-dot {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 16px;
  height: 16px;
  background: #4CAF50;
  border: 3px solid white;
  border-radius: 50%;
}

.mobile-panel__info {
  flex: 1;
  min-width: 0;
}

.mobile-panel__title {
  font-weight: 600;
  font-size: 20px;
  color: #111827;
  margin: 0 0 4px 0;
  line-height: 1.3;
}

.mobile-panel__subtitle {
  font-size: 16px;
  color: #6B7280;
  margin: 0;
  line-height: 1.4;
}

.mobile-panel__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #F3F4F6;
  border: none;
  border-radius: 20px;
  color: #6B7280;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.mobile-panel__close:active {
  background: #E5E7EB;
  transform: scale(0.95);
}

/* Mobile Options */
.mobile-options {
  flex: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.mobile-option {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 20px;
  background: white;
  border: 2px solid #E5E7EB;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
}

.mobile-option:active {
  transform: scale(0.98);
  border-color: #374151;
}

.mobile-option--chat {
  background: linear-gradient(135deg, #F3F4F6, #E5E7EB);
  border-color: #E5E7EB;
  color: #374151;
}

.mobile-option--chat.mobile-option--closed {
  background: linear-gradient(135deg, #9CA3AF, #6B7280);
  border-color: #9CA3AF;
  color: #E5E7EB;
  cursor: not-allowed;
  opacity: 0.7;
}

.mobile-option--chat.mobile-option--closed:active {
  transform: none;
  border-color: #9CA3AF;
}

.mobile-option--chat.mobile-option--closed .mobile-option__icon {
  background: rgba(255, 255, 255, 0.1);
}

.mobile-option--chat.mobile-option--closed .mobile-option__icon svg {
  color: #D1D5DB;
}

.mobile-option--chat.mobile-option--closed .mobile-option__title {
  color: #D1D5DB;
}

.mobile-option--chat.mobile-option--closed .mobile-option__subtitle {
  color: rgba(255, 255, 255, 0.5);
}

.mobile-option--chat .mobile-option__icon {
  background: #6B7280;
}

.mobile-option__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  background: #F3F4F6;
  border-radius: 16px;
  flex-shrink: 0;
}

.mobile-option__icon svg {
  width: 28px;
  height: 28px;
  color: #374151;
}

.mobile-option--chat .mobile-option__icon svg {
  color: white;
}

.mobile-option__content {
  flex: 1;
  min-width: 0;
}

.mobile-option__title {
  font-weight: 600;
  font-size: 18px;
  color: #111827;
  margin: 0 0 4px 0;
  line-height: 1.3;
}

.mobile-option--chat .mobile-option__title {
  color: #374151;
}

.mobile-option__subtitle {
  font-size: 14px;
  color: #6B7280;
  margin: 0;
  line-height: 1.4;
}

.mobile-option--chat .mobile-option__subtitle {
  color: #6B7280;
}

.mobile-option__arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: #9CA3AF;
  flex-shrink: 0;
}

.mobile-option--chat .mobile-option__arrow {
  color: #6B7280;
}

.mobile-option__arrow svg {
  width: 16px;
  height: 16px;
}

.mobile-option__badge {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(239, 68, 68, 0.2);
  border-radius: 20px;
  padding: 4px 12px;
  flex-shrink: 0;
}

.mobile-option__badge-text {
  font-size: 12px;
  font-weight: 600;
  color: #EF4444;
}

/* Mobile Actions */
.mobile-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.mobile-actions--primary {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 8px;
}

.mobile-action {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px 16px;
  background: white;
  border: 2px solid #E5E7EB;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mobile-action:active {
  transform: scale(0.95);
  border-color: #6B7280;
}

.mobile-action--primary {
  padding: 24px 20px;
  border: 2px solid transparent;
  background: linear-gradient(135deg, #F3F4F6, #E5E7EB);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  gap: 12px;
}

.mobile-action--primary:active {
  transform: scale(0.98);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.mobile-action__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: #F3F4F6;
  border-radius: 12px;
  transition: all 0.2s ease;
}

.mobile-action--phone .mobile-action__icon {
  background: var(--color-lender-secondary, #EFF6FF);
}

.mobile-action--email .mobile-action__icon {
  background: #FEE2E2;
}

.mobile-action--primary.mobile-action--phone .mobile-action__icon {
  background: linear-gradient(135deg, var(--color-lender-primary, #4B83DB), var(--color-lender-accent, #3B72C5));
  width: 56px;
  height: 56px;
  box-shadow: 0 4px 12px rgba(75, 131, 219, 0.3);
}

.mobile-action--primary.mobile-action--email .mobile-action__icon {
  background: linear-gradient(135deg, var(--color-primary, #DB0908), var(--color-primary-dark, #C00202));
  width: 56px;
  height: 56px;
  box-shadow: 0 4px 12px rgba(219, 9, 8, 0.3);
}

.mobile-action__icon svg {
  width: 24px;
  height: 24px;
  color: #374151;
  transition: all 0.2s ease;
}

.mobile-action--phone .mobile-action__icon svg {
  color: var(--color-lender-primary, #4B83DB);
}

.mobile-action--email .mobile-action__icon svg {
  color: var(--color-primary, #DB0908);
}

.mobile-action--primary .mobile-action__icon svg {
  color: white;
  width: 28px;
  height: 28px;
}

.mobile-action__label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  text-align: center;
  transition: all 0.2s ease;
}

.mobile-action--primary .mobile-action__label {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.mobile-action__sublabel {
  font-size: 13px;
  font-weight: 500;
  color: #6B7280;
  text-align: center;
  line-height: 1.3;
  margin-top: -4px;
}

/* Mobile Footer */
.mobile-panel__footer {
  padding: 20px;
  border-top: 1px solid #E5E7EB;
  background: #F9FAFB;
  flex-shrink: 0;
}

/* Animations */
@keyframes mobile-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(55, 65, 81, 0.5);
  }
  70% {
    box-shadow: 0 0 0 20px rgba(55, 65, 81, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(55, 65, 81, 0);
  }
}

@keyframes pulse-dot {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

@keyframes swipe-hint {
  0%, 90%, 100% {
    transform: translateY(0);
    opacity: 0.6;
  }
  10%, 20% {
    transform: translateY(2px);
    opacity: 1;
  }
}

/* Transitions */
.mobile-fab-enter-active,
.mobile-fab-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.mobile-fab-enter-from,
.mobile-fab-leave-to {
  opacity: 0;
  transform: scale(0.8) translateY(10px);
}

.mobile-panel-enter-active,
.mobile-panel-leave-active {
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.mobile-panel-enter-from {
  opacity: 0;
  transform: translateY(100%);
}

.mobile-panel-leave-to {
  opacity: 0;
  transform: translateY(100%);
}
</style> 